// Implements: F-UIX-020, F-UIX-007, F-DSG-009
/**
 * The editor's "Activo en" field (mockup v3 P5: one `.sw-line` per scope the person manages, and the
 * help under them). Pure presentation.
 *
 * `ActiveInGroup` is the field: its label names the group and the help says what activating does.
 * `ActiveInSwitchLine` is one scope: a switch named by the scope, what the scope uses now beside it
 * ("Activo", "Ahora: «…»" or "Diseño del sistema"), the reason when the profile cannot serve that
 * scope (the switch is then disabled, unless the profile is active there and can be switched off), and a refusal as an alert under it. `ActiveInReadOnlyLine` is
 * a scope the person cannot manage, listed as text while the profile is active there.
 */

import { useId } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Switch } from "../../../shared/ui/atoms/Switch";
import styles from "./ActiveInField.module.css";

export function ActiveInGroup({ children }: { readonly children: ReactNode }): ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <div className={styles.field} role="group" aria-labelledby={titleId}>
      <span id={titleId} className={styles.label}>
        {t("design.activeIn.label")}
      </span>
      <ul className={styles.lines}>{children}</ul>
      <p className={styles.hint}>{t("design.activeIn.help")}</p>
    </div>
  );
}

export interface ActiveInSwitchLineProps {
  readonly label: string;
  readonly checked: boolean;
  readonly pending: boolean;
  /** The server lists the scope in the profile's `activatable_in`. */
  readonly allowed: boolean;
  /** What the scope uses now, when the profile is not the one: "Ahora: «…»" or the system's. */
  readonly meta: string | null;
  readonly error: string | null;
  readonly onChange: (checked: boolean) => void;
}

export function ActiveInSwitchLine({
  label,
  checked,
  pending,
  allowed,
  meta,
  error,
  onChange,
}: ActiveInSwitchLineProps): ReactElement {
  const { t } = useTranslation();
  const metaId = useId();
  const status = checked ? t("design.activeIn.on") : allowed ? meta : t("design.activeIn.notAllowed");
  return (
    <li className={styles.line} aria-busy={pending}>
      <Switch
        label={label}
        checked={checked}
        onChange={onChange}
        // An active mark outside `activatable_in` can still be removed: only turning ON is refused.
        disabled={pending || (!allowed && !checked)}
        describedBy={status === null ? undefined : metaId}
      />
      {status === null ? null : (
        <span id={metaId} className={styles.meta}>
          {status}
        </span>
      )}
      {error === null ? null : (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
    </li>
  );
}

export function ActiveInReadOnlyLine({ label }: { readonly label: string }): ReactElement {
  const { t } = useTranslation();
  return (
    <li className={styles.line}>
      <p className={styles.readOnly}>{t("design.activeIn.readOnly", { scope: label })}</p>
    </li>
  );
}
